import { BufferGeometry, Float32BufferAttribute, Points, PointsMaterial, ShaderMaterial, TextureLoader, Vector3, use } from '@anov/3d-core'
import type { SceneControl } from '@anov/3d-core'
import type { ISnowOption } from '../../utils/constant'
import { Snow_Icon } from '../../utils/constant'

const _defaultOption: ISnowOption = {
  range: 1000,
  count: 10000,
  opacity: 0.6,
  icon: Snow_Icon,
  size: 10,
  speed: [0, 2, 0],
}

class SnowMesh extends Points {
  option: ISnowOption = _defaultOption
  sceneControl: SceneControl

  constructor(opt: ISnowOption = {}, sceneControl: SceneControl) {
    super()
    this.sceneControl = sceneControl
    Object.assign(this.option, opt)

    const { range, count, size, icon, opacity } = this.option
    const material = new PointsMaterial({
      size,
      map: new TextureLoader().load(icon!),
      transparent: true,
      opacity,
      depthTest: false,
    })

    this.geometry = this.getPointGeometry(count!, range!)
    this.material = material

    this.renderOrder = 1
  }

  private getPointGeometry(count: number, range: number) {
    const bufferGeometry = new BufferGeometry()
    const vertices = []

    for (let i = 0; i < count!; i++) {
      vertices.push(
        Math.random() * range! - range! / 2,
        Math.random() * range!,
        Math.random() * range! - range! / 2,
      )
    }
    bufferGeometry.setAttribute('position', new Float32BufferAttribute(vertices, 3))
    return bufferGeometry
  }

  setIcon(icon: string) {
    this.option.icon = icon;
    (this.material as PointsMaterial).map = new TextureLoader().load(icon)
  }

  setSize(size: number) {
    this.option.size = size;
    (this.material as PointsMaterial).size = size
  }

  setCount(count: number) {
    this.option.count = count
    this.geometry = this.getPointGeometry(count!, this.option.range!)
  }

  setRange(range: number) {
    this.option.range = range
    this.geometry = this.getPointGeometry(this.option.count!, range!)
  }

  setSpeed(speed: number | number[]) {
    if (typeof speed === 'number')
      this.option.speed = [0, speed, 0]
    else
      this.option.speed = speed
  }

  public setEffect(ops: ISnowOption) {
    const { icon, size, count, speed, range } = ops
    Object.keys(ops).forEach((key: string) => {
      switch (key) {
        case 'icon':
          this.setIcon(icon!)
          break
        case 'size':
          this.setSize(size!)
          break
        case 'range':
          this.setRange(range!)
          break
        case 'count':
          this.setCount(count!)
          break
        case 'speed':
          this.setSpeed(speed!)
          break
      }
    })
  }

  stop() {
    this.visible = false
  }

  continue() {
    this.visible = true
  }

  update() {
    const positions = this.geometry.attributes.position.array

    const { speed, range } = this.option
    const [x, y, z] = speed!

    for (let i = 0; i < positions.length; i += 3) {
      positions[i] += x
      positions[i + 1] -= y
      positions[i + 2] += z

      if (positions[i + 1] <= range! / -2)
        positions[i + 1] = range! / 2
    }

    this.geometry.attributes.position.needsUpdate = true
  }

  delete() {
    this.remove()
    this.removeFromParent()
  }
}

export default SnowMesh